import { FunctionComponent, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Avatar,
    Badge,
    Box,
    Button,
    Card,
    CardBody,
    CardFooter,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Icon,
    Radio,
    RadioGroup,
    Select,
    Skeleton,
    Stack,
    Text,
    Tooltip,
    useClipboard,
    useDisclosure,
    VStack,
} from "@chakra-ui/react";
import { IconArrowLeft, IconShieldPlus, IconUser, IconUsers } from "@tabler/icons-react";
import { ApplicationRole, TenantRole, useCreateUserTenantRole, useTenants } from "../../api";
import { useUser, useUserTenants } from "../../api/users";
import { TenantRoleControl } from "../AdminUsersPage/components/TenantRoleControl";
import { RoleNames } from "../../hooks/useHasAccess";
import { SetPageTitle } from "../../components/SetPageTitle";
import { formatTimestampStr } from "@am-tax/fe-core";
import { Controller, useForm } from "react-hook-form";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

type UserSection = "overview" | "access";

const avatarColors = ["blue", "teal", "green", "purple", "orange", "pink", "cyan", "red"];

function getAvatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
}

const roleColorScheme: Record<ApplicationRole, string> = {
    [ApplicationRole.admin]: "red",
    [ApplicationRole.developer]: "purple",
    [ApplicationRole.contributor]: "blue",
    [ApplicationRole.reader]: "gray",
};

export const AdminUserDetailPage: FunctionComponent = () => {
    const { userId, section } = useParams<{ userId: string; section: string }>();
    const navigate = useNavigate();
    const appInsights = useAppInsightsContext();
    const activeSection: UserSection = (section as UserSection) ?? "overview";

    const { isLoading, data: user } = useUser(userId ?? "");

    useEffect(() => {
        appInsights.trackPageView({ uri: `/admin/users/${userId}` });
    }, [appInsights, userId]);

    const displayName = user?.displayName ?? "";
    const avatarColor = getAvatarColor(displayName);
    const initials = displayName
        .split(" ")
        .map(p => p[0] ?? "")
        .join("")
        .toUpperCase()
        .slice(0, 2);

    const navItems: { id: UserSection; label: string; icon: React.ReactNode }[] = [
        { id: "overview", label: "Overview", icon: <IconUser size={16} /> },
        { id: "access", label: "Client Access", icon: <IconUsers size={16} /> },
    ];

    return (
        <Box p={8} maxW="1000px" mx="auto">
            <SetPageTitle title={displayName ? `${displayName} — User` : "User Detail"} />

            <Button
                variant="ghost"
                size="sm"
                leftIcon={<IconArrowLeft size={16} />}
                onClick={() => navigate("/admin/users")}
                mb={6}
                color="gray.600"
                _hover={{ bg: "gray.100" }}
            >
                Back to Users
            </Button>

            <Flex gap={10} align="flex-start">
                {/* Left sidebar */}
                <Box w="240px" flexShrink={0} position="sticky" top={8}>
                    <Skeleton isLoaded={!isLoading}>
                        <VStack spacing={3} align="center" mb={6}>
                            <Avatar size="xl" name={displayName || "?"} bg={`${avatarColor}.500`} color="white" getInitials={() => initials} />
                            <VStack spacing={0} align="center">
                                <Text fontWeight="semibold" fontSize="md" textAlign="center">
                                    {displayName || "—"}
                                </Text>
                                <Text fontSize="sm" color="gray.500" wordBreak="break-all" textAlign="center">
                                    {user?.email}
                                </Text>
                            </VStack>
                            <HStack flexWrap="wrap" justify="center">
                                {(user?.appRoles ?? []).map(role => (
                                    <Badge key={role} colorScheme={roleColorScheme[role]} borderRadius="full" fontSize="xs">
                                        {RoleNames[role]}
                                    </Badge>
                                ))}
                            </HStack>
                        </VStack>
                    </Skeleton>

                    <Divider mb={3} />

                    <VStack spacing={1} align="stretch">
                        {navItems.map(item => {
                            const isActive = activeSection === item.id;
                            return (
                                <Flex
                                    key={item.id}
                                    as="button"
                                    onClick={() => navigate(`/admin/users/${userId}/${item.id}`)}
                                    align="center"
                                    gap={2.5}
                                    px={3}
                                    py={2}
                                    borderRadius="md"
                                    borderLeft={isActive ? "3px solid" : "3px solid transparent"}
                                    borderColor={isActive ? "blue.500" : "transparent"}
                                    bg={isActive ? "blue.50" : "transparent"}
                                    color={isActive ? "blue.700" : "gray.700"}
                                    fontWeight={isActive ? "semibold" : "normal"}
                                    fontSize="sm"
                                    textAlign="left"
                                    transition="all 0.15s"
                                    _hover={{ bg: isActive ? "blue.50" : "gray.100" }}
                                    w="full"
                                >
                                    <Box flexShrink={0} color={isActive ? "blue.500" : "gray.500"}>
                                        {item.icon}
                                    </Box>
                                    {item.label}
                                </Flex>
                            );
                        })}
                    </VStack>
                </Box>

                {/* Right content */}
                <Box flex={1} minW={0}>
                    {activeSection === "overview" && <UserOverviewSection userId={userId ?? ""} user={user} isLoading={isLoading} />}
                    {activeSection === "access" && userId && <UserAccessSection userId={userId} />}
                </Box>
            </Flex>
        </Box>
    );
};

const SectionHeader: FunctionComponent<{ title: string; description?: string }> = ({ title, description }) => (
    <Box mb={6}>
        <Heading size="md" mb={1}>
            {title}
        </Heading>
        {description && (
            <Text fontSize="sm" color="gray.500">
                {description}
            </Text>
        )}
        <Divider mt={4} />
    </Box>
);

const FieldRow: FunctionComponent<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box>
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="gray.500" mb={1}>
            {label}
        </Text>
        {children}
    </Box>
);

const UserOverviewSection: FunctionComponent<{ userId: string; user: ReturnType<typeof useUser>["data"]; isLoading: boolean }> = ({
    userId,
    user,
    isLoading,
}) => {
    const idClipboard = useClipboard(userId);

    return (
        <Box>
            <SectionHeader title="Overview" description="Account information synced from the identity provider." />
            <Skeleton isLoaded={!isLoading}>
                <VStack spacing={6} align="stretch">
                    <FieldRow label="Display Name">
                        <Text fontSize="sm">{user?.displayName ?? "—"}</Text>
                    </FieldRow>
                    <FieldRow label="Email">
                        <Text fontSize="sm">{user?.email ?? "—"}</Text>
                    </FieldRow>
                    <FieldRow label="User ID">
                        <Tooltip label={idClipboard.hasCopied ? "Copied!" : "Click to copy"} placement="right">
                            <Text fontSize="sm" fontFamily="mono" color="gray.600" cursor="copy" onClick={idClipboard.onCopy} display="inline-block">
                                {userId}
                            </Text>
                        </Tooltip>
                    </FieldRow>
                    <FieldRow label="Registered On">
                        <Text fontSize="sm">{user ? formatTimestampStr(user.createdOn) : "—"}</Text>
                    </FieldRow>
                    <FieldRow label="Registration">
                        {user?.isSelfRegistered ? (
                            <Badge colorScheme="orange" borderRadius="full">
                                Self-registered
                            </Badge>
                        ) : (
                            <Badge colorScheme="green" borderRadius="full">
                                AD-provisioned
                            </Badge>
                        )}
                    </FieldRow>
                    <FieldRow label="App Roles">
                        <HStack mt={1} flexWrap="wrap">
                            {(user?.appRoles ?? []).map(role => (
                                <Badge key={role} colorScheme={roleColorScheme[role]} borderRadius="full">
                                    {RoleNames[role]}
                                </Badge>
                            ))}
                            {(user?.appRoles ?? []).length === 0 && (
                                <Text fontSize="sm" color="gray.400">
                                    No roles assigned
                                </Text>
                            )}
                        </HStack>
                    </FieldRow>
                </VStack>
            </Skeleton>
        </Box>
    );
};

const UserAccessSection: FunctionComponent<{ userId: string }> = ({ userId }) => {
    const { isLoading, data: userTenants } = useUserTenants(userId);

    return (
        <Box>
            <SectionHeader title="Client Access" description="Manage which clients this user can access and their role within each." />
            <Flex direction="column" gap={4}>
                <AddAccessFormCard userId={userId} />
                <Skeleton isLoaded={!isLoading} height="20px" />
                <Flex direction="column" gap={2}>
                    {(userTenants ?? []).map(m => (
                        <TenantRoleControl key={m.tenantId} {...m} />
                    ))}
                    {!isLoading && (userTenants ?? []).length === 0 && (
                        <Text fontSize="sm" color="gray.400">
                            No client access assigned.
                        </Text>
                    )}
                </Flex>
            </Flex>
        </Box>
    );
};

const AddAccessFormCard: FunctionComponent<{ userId: string }> = ({ userId }) => {
    const addAccessSection = useDisclosure();
    const tenants = useTenants();
    const userTenants = useUserTenants(userId);
    const createUserTenantRole = useCreateUserTenantRole();

    const form = useForm({
        defaultValues: {
            tenantId: "",
            role: TenantRole.Contributor,
        },
    });

    const tenantOptions =
        tenants.isLoading || userTenants.isLoading
            ? []
            : (tenants.data ?? [])
                  .filter(t => !(userTenants.data ?? []).find(m => m.tenantId === t.id))
                  .map(t => (
                      <option key={t.id} value={t.id}>
                          {t.name}
                      </option>
                  ));

    return (
        <>
            <Button
                alignSelf="flex-start"
                leftIcon={<Icon as={IconShieldPlus} />}
                onClick={() => {
                    form.reset();
                    addAccessSection.onToggle();
                }}
            >
                {addAccessSection.isOpen ? "Cancel" : "Add Access"}
            </Button>

            {addAccessSection.isOpen && (
                <Card>
                    <form
                        onSubmit={form.handleSubmit(data => {
                            createUserTenantRole.mutate({
                                tenantId: data.tenantId,
                                userId,
                                role: data.role,
                                expiresOn: undefined,
                            });
                        })}
                    >
                        <CardBody>
                            <VStack gap="4">
                                <FormControl>
                                    <FormLabel>Client</FormLabel>
                                    <Select placeholder="Select client" {...form.register("tenantId")}>
                                        {tenantOptions}
                                    </Select>
                                </FormControl>
                                <Controller
                                    control={form.control}
                                    name="role"
                                    render={({ field }) => (
                                        <FormControl as="fieldset">
                                            <FormLabel as="legend">Role</FormLabel>
                                            {/* @ts-ignore */}
                                            <RadioGroup value={field.value} onChange={field.onChange}>
                                                <Stack spacing={5} direction="row">
                                                    <Radio value={TenantRole.Owner}>Owner</Radio>
                                                    <Radio value={TenantRole.Contributor}>Contributor</Radio>
                                                    <Radio value={TenantRole.Reader}>Reader</Radio>
                                                </Stack>
                                            </RadioGroup>
                                        </FormControl>
                                    )}
                                />
                            </VStack>
                        </CardBody>
                        <CardFooter justifyContent="flex-end">
                            <Button type="submit" leftIcon={<Icon as={IconShieldPlus} />} colorScheme="blue">
                                Add
                            </Button>
                        </CardFooter>
                    </form>
                </Card>
            )}
        </>
    );
};
